import styled, { keyframes } from "styled-components";
import GalleryImg from "../../../assets/images/hero/hero-img.jpg";
import { HeaderLeft, HeadingBlock } from "../../../common/components/layout/HeadingBlock";
import Eyebrow from "../../../common/components/eyebrow/Eyebrow";
import H2 from "../../../common/components/text/H2";
import Description from "../../../common/components/text/Description";
import { Theme } from "../../../common/themes/theme";


const galleryItems = [
  {
    img: GalleryImg,
    tag: "Aerospace",
    name: "Structural\nBracket",
    spec: "5-Axis",
    specKey: "Machining",
    className: "cell-1",
    accent: "#e63329",
  },
  {
    img: GalleryImg,
    tag: "Medical",
    name: "Implant Housing",
    spec: "Ra 0.4",
    specKey: "Surface Finish",
    className: "cell-2",
    accent: "#e63329",
  },
  {
    img: GalleryImg,
    tag: "Automotive",
    name: "Drive Shaft",
    spec: "Ti-6Al",
    specKey: "Material",
    className: "cell-3",
    accent: "#e63329",
  },
  {
    img: GalleryImg,
    tag: "Electronics",
    name: "Heat Sink",
    spec: "H7/g6",
    specKey: "Fit Tolerance",
    className: "cell-4",
    accent: "#e63329",
  },
  {
    img: GalleryImg,
    tag: "Energy",
    name: "Valve Body",
    spec: "CMM",
    specKey: "Verified",
    className: "cell-5",
    accent: "#e63329",
  },
];

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.section`
  background: ${Theme.color.background.light};
  padding: 80px 50px 100px;

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    padding: 60px 20px 80px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 48px;
  gap: 32px;

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ToleranceBadge = styled.div`
  flex-shrink: 0;
  position: relative;
  border: 1px solid ${Theme.color.border.light};
  padding: 18px 26px;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      105deg,
      transparent 40%,
      rgba(230, 51, 41, 0.07) 50%,
      transparent 60%
    );
    background-size: 200% auto;
    animation: ${shimmer} 3.5s linear infinite;
  }
`;

const ToleranceNum = styled.p`
  font-family: ${Theme.font.fontFamily.heading};
  font-size: ${Theme.font.fontSize.xxl};
  font-weight: ${Theme.font.fontWeight.bold};
  color: ${Theme.color.text.heading};
  line-height: 1;
  margin: 0;

  span {
    font-size: 15px;
    color: ${Theme.color.text.primary};
    margin-left: 2px;
  }
`;

const ToleranceLabel = styled.p`
  font-size: ${Theme.font.fontSize.xs};
  font-weight: ${Theme.font.fontWeight.medium};
  letter-spacing: ${Theme.font.letterSpacing.widest};
  text-transform: uppercase;
  color: ${Theme.color.text.body};
  margin: 6px 0 0;
`;

const BentoGrid = styled.div`
  display: grid;
  gap: 10px;

  grid-template-columns: 2fr 1.1fr 1.1fr;
  grid-template-rows: 360px 260px;

  .cell-1 {
    grid-column: 1;
    grid-row: 1 / 3;
  }
  .cell-2 {
    grid-column: 2;
    grid-row: 1;
  }
  .cell-3 {
    grid-column: 3;
    grid-row: 1;
  }
  .cell-4 {
    grid-column: 2;
    grid-row: 2;
  }
  .cell-5 {
    grid-column: 3;
    grid-row: 2;
  }

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: repeat(3, 240px);

    .cell-1 {
      grid-column: 1 / 3;
      grid-row: 1;
    }
    .cell-2 {
      grid-column: 1;
      grid-row: 2;
    }
    .cell-3 {
      grid-column: 2;
      grid-row: 2;
    }
    .cell-4 {
      grid-column: 1;
      grid-row: 3;
    }
    .cell-5 {
      grid-column: 2;
      grid-row: 3;
    }
  }

  @media (max-width: ${Theme.breakpoints.MOBILE}px) {
    grid-template-columns: 1fr;
    grid-template-rows: 260px repeat(4, 200px);

    .cell-1 {
      grid-column: 1;
      grid-row: 1;
    }
    .cell-2 {
      grid-column: 1;
      grid-row: 2;
    }
    .cell-3 {
      grid-column: 1;
      grid-row: 3;
    }
    .cell-4 {
      grid-column: 1;
      grid-row: 4;
    }
    .cell-5 {
      grid-column: 1;
      grid-row: 5;
    }
  }
`;

const Cell = styled.div`
  position: relative;
  overflow: hidden;
  background: ${Theme.color.background.mid};
  cursor: pointer;
  animation: ${fadeUp} 0.5s ease both;
  border-radius: ${Theme.border.borderRadius.md};

  &.cell-1 {
    animation-delay: 0.05s;
  }
  &.cell-2 {
    animation-delay: 0.1s;
  }
  &.cell-3 {
    animation-delay: 0.15s;
  }
  &.cell-4 {
    animation-delay: 0.2s;
  }
  &.cell-5 {
    animation-delay: 0.25s;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }

  &:hover img {
    transform: scale(1.06);
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border: 1.5px solid transparent;
    transition: border-color 0.25s ease;
    pointer-events: none;
    z-index: 5;
  }

  &:hover::after {
    border-color: ${Theme.color.text.primary};
  }
`;

const CellOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    160deg,
    transparent 30%,
    rgba(8, 8, 10, 0.72) 100%
  );
  pointer-events: none;
  z-index: 1;
`;

const SpecBadge = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 3;
  background: rgba(10, 10, 12, 0.82);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 7px 11px;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const SpecVal = styled.p`
  font-family: ${Theme.font.fontFamily.heading};
  font-size: ${Theme.font.fontSize.md};
  font-weight: ${Theme.font.fontWeight.bold};
  color: #fff;
  margin: 0;
  line-height: 1.1;
`;

const SpecKey = styled.p`
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
`;

const CellLabel = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CellTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: ${Theme.font.fontWeight.medium};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #fff;
  background: ${Theme.color.text.primary};
  padding: 3px 8px;
  align-self: flex-start;
`;

const CellName = styled.p`
  font-family: ${Theme.font.fontFamily.heading};
  font-size: ${Theme.font.fontSize.lg};
  font-weight: ${Theme.font.fontWeight.bold};
  text-transform: uppercase;
  color: #fff;
  letter-spacing: 0.04em;
  line-height: ${Theme.font.lineHeight.tight};
  margin: 0;
  white-space: pre-line;
`;

function ExpertiseSection() {
  return (
    <Wrapper>
      <Header>
        <HeaderLeft>
          <HeadingBlock>
            <Eyebrow text={"Our Industry Experience"} />
            <H2>
              Precision You
              <br />
              Can <span>See.</span>
            </H2>
            <Description>
              Every component we produce is a testament to our engineering
              standards. From aerospace brackets to medical implant housings —
              tolerances don't lie.
            </Description>
          </HeadingBlock>
        </HeaderLeft>

        <ToleranceBadge>
          <ToleranceNum>
            ±0.001<span>mm</span>
          </ToleranceNum>
          <ToleranceLabel>Standard Tolerance</ToleranceLabel>
        </ToleranceBadge>
      </Header>

      <BentoGrid>
        {galleryItems.map((item) => (
          <Cell key={item.className} className={item.className}>
            <img src={item.img} alt={item.name} />
            <CellOverlay />
            <SpecBadge>
              <SpecVal>{item.spec}</SpecVal>
              <SpecKey>{item.specKey}</SpecKey>
            </SpecBadge>
            <CellLabel>
              <CellTag>{item.tag}</CellTag>
              <CellName>{item.name}</CellName>
            </CellLabel>
          </Cell>
        ))}
      </BentoGrid>
    </Wrapper>
  );
}

export default ExpertiseSection;
